import React, { useState } from "react";
import axios from "axios";
import apiConfig from "../../config/api.js";

/**
 * Demande de réinitialisation : envoi d’un lien par email au propriétaire du compte.
 * Seul celui qui a accès à la boîte mail peut réinitialiser le mot de passe.
 */
const ForgotPasswordForm = ({ onBack, onClose }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [resetLink, setResetLink] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);
    setResetLink(null);
    try {
      const response = await axios.post(
        `${apiConfig.baseURL}/users/forgot-password`,
        { email },
      );
      if (response.data.success) {
        setSuccess(true);
        if (response.data.resetLink) setResetLink(response.data.resetLink);
      }
    } catch (err) {
      console.error("Erreur lors de la demande de réinitialisation:", err);
      setError(
        err.response?.data?.message ||
          "Erreur lors de la demande de réinitialisation",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[9999] animate-fade-in">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full animate-slide-in-up">
        {/* En-tête */}
        <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-6 py-6 rounded-t-3xl">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">🔑 Mot de passe oublié</h2>
              <p className="text-orange-100 text-sm mt-1">
                Entrez votre email pour réinitialiser votre mot de passe
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white hover:bg-white/20 p-2 rounded-full transition-all duration-200"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Message de succès */}
          {success && (
            <div className="mb-4 space-y-3">
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl">
                <div className="flex items-center">
                  <span className="text-green-500 mr-2">✅</span>
                  <div>
                    <p className="font-semibold">
                      {resetLink
                        ? "Lien créé (mode développement)"
                        : "Email envoyé !"}
                    </p>
                    <p className="text-sm mt-1">
                      {resetLink
                        ? "Aucun email n'a été envoyé (SMTP non configuré). Cliquez sur le lien ci-dessous pour réinitialiser votre mot de passe :"
                        : "Si un compte existe avec cet email, vous recevrez un lien. Vérifiez votre boîte de réception."}
                    </p>
                  </div>
                </div>
              </div>
              {resetLink && (
                <a
                  href={resetLink}
                  className="block w-full px-4 py-3 bg-orange-500 hover:bg-orange-600 text-white text-center font-semibold rounded-xl transition-colors"
                >
                  Réinitialiser mon mot de passe →
                </a>
              )}
            </div>
          )}

          {/* Message d'erreur */}
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
              <div className="flex items-center">
                <span className="text-red-500 mr-2">❌</span>
                {error}
              </div>
            </div>
          )}

          {!success && (
            <>
              {/* Email */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  📧 Adresse email
                </label>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-300 text-base"
                  placeholder="votre.email@example.com"
                  required
                />
                <p className="text-xs text-gray-500 mt-2">
                  Nous vous enverrons un lien pour réinitialiser votre mot de
                  passe
                </p>
              </div>

              {/* Boutons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Envoi...
                    </div>
                  ) : (
                    "📧 Envoyer le lien"
                  )}
                </button>

                <button
                  type="button"
                  onClick={onBack}
                  className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-all duration-300 transform hover:scale-105"
                >
                  Retour
                </button>
              </div>
            </>
          )}

          {success && (
            <div className="mt-4">
              <button
                type="button"
                onClick={onBack}
                className="w-full px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-all duration-300 transform hover:scale-105"
              >
                Retour à la connexion
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default ForgotPasswordForm;
